/**
 * お問い合わせ画面の表示用の変換（純粋関数）。
 * 日時は日本時間（Asia/Tokyo）で出す（サーバーは UTC の ISO 文字列で返す）。
 */

const TZ = 'Asia/Tokyo';

/** 一覧の状態の表示。返信が未読 → 返信あり、対応済み → 対応済み、最後が開発者 → 返信済み、それ以外 → 受付済み。 */
export function statusOf(row) {
  if (row?.unread) return { key: 'reply', label: '返信あり' };
  if (row?.status === 'closed') return { key: 'closed', label: '対応済み' };
  if (row?.lastMessageBy === 'dev') return { key: 'replied', label: '返信済み' };
  return { key: 'open', label: '受付済み' };
}

function parts(iso) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return null;
  const fmt = new Intl.DateTimeFormat('ja-JP', {
    timeZone: TZ, month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false, weekday: 'short',
  });
  return Object.fromEntries(fmt.formatToParts(d).map((p) => [p.type, p.value]));
}

/** 「10/26 12:03」。日時が読めなければ空文字。 */
export function formatWhen(iso) {
  const p = parts(iso);
  return p ? `${p.month}/${p.day} ${p.hour}:${p.minute}` : '';
}

/** 「12:03」（同じ日の中の時刻）。 */
export function formatTime(iso) {
  const p = parts(iso);
  return p ? `${p.hour}:${p.minute}` : '';
}

/** 日付の区切り用「10月26日（月）」。日付ごとに同じ文字列になる。 */
export function dayLabel(iso) {
  const p = parts(iso);
  return p ? `${p.month}月${p.day}日（${p.weekday}）` : '';
}

/**
 * やり取りを、日付ごとのまとまりに分ける（チャットの日付の区切りを入れるため）。
 * @returns {{label: string, messages: object[]}[]} 古い順。日時が読めないものは、前のまとまりに入れる。
 */
export function groupByDay(messages) {
  const groups = [];
  (messages || []).forEach((m) => {
    const label = dayLabel(m.createdAt) || groups[groups.length - 1]?.label || '';
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.messages.push(m);
    else groups.push({ label, messages: [m] });
  });
  return groups;
}

/** 発言者の表示名。開発者側は「KUROCO 開発チーム」、本人は「あなた」。 */
export function speakerName(from) {
  return from === 'dev' ? 'KUROCO 開発チーム' : 'あなた';
}

/** 開発者の側から見た状態。対応済み / 返信待ち（最後の発言が本人）/ 返信済み。 */
export function devStatusOf(row) {
  if (row?.status === 'closed') return { key: 'closed', label: '対応済み' };
  if (row?.lastMessageBy === 'user') return { key: 'waiting', label: '返信待ち' };
  return { key: 'replied', label: '返信済み' };
}

/**
 * 発言者の表示名。viewer = 見ている人（'user' 本人 / 'dev' 開発者）。
 * 本人から見ると「あなた」と「KUROCO 開発チーム」。開発者から見ると、本人は名前（無ければ「ユーザー」）。
 */
export function speakerLabel(from, viewer = 'user', authorName = '') {
  if (viewer === 'dev') return from === 'dev' ? 'KUROCO 開発チーム（あなた）' : authorName || 'ユーザー';
  return speakerName(from);
}
